/** UI-only RHF keys that must never be sent to APIs. */
const UI_ONLY_ROOT_KEYS = ['__embeddedTableDirty', '__documentStoreDirty'] as const;

function stripUiOnlyRootKeys(obj: Record<string, unknown>): void {
  for (const key of UI_ONLY_ROOT_KEYS) {
    delete obj[key];
  }
}

/**
 * Payload sanitizer & hydrator — metadata-driven cleanup pipeline.
 *
 * Both directions use the same layout-tree walk to discover field metadata.
 *
 * OUTBOUND (sanitizePayload — form → API):
 *   1. Strip dashes from phone / SSN fields (API wants raw digits)
 *   2. Coerce boolean radios (string 'true'/'false' → boolean)
 *   3. Trim all strings
 *   4. Empty optional strings → null
 *
 * INBOUND (hydratePayload — API → form):
 *   1. Add dashes to phone / SSN fields (form displays formatted values)
 *   2. Stringify booleans for radio fields (API sends boolean, form uses string)
 *   3. Inject defaults for hidden fields
 *   4. Initialize UDF namespace objects
 *   5. Wrap in schema namespace
 *
 * Hidden fields are NOT stripped by the outbound sanitizer — they may be needed
 * by entity-specific transforms for conditional logic. The entity transform is
 * responsible for deleting UI-only keys.
 */

import type { FormLayoutNode, FormFieldsNode, FormEachNode, FormUdfNode } from '../types/layout';
import type { FormFieldMapping, FieldDef } from '../types/fieldMapping';

// ---------------------------------------------------------------------------
// Field metadata collected from the layout tree
// ---------------------------------------------------------------------------

interface FieldMeta {
  def: FieldDef;
  required: boolean;
  hidden: boolean;
  adapterHint?: string;
  /** When set, wrap/unwrap array items as `{ [arrayItemKey]: value }` on outbound/inbound. */
  arrayItemKey?: string;
  /**
   * Constant value to inject for hidden fields (e.g. `referenceType: 'Participant'`).
   * Hidden fields are never registered by RHF (no Controller rendered), so their value
   * is absent from form state. The sanitizer injects this at outbound time.
   */
  defaultValue?: unknown;
  /**
   * Synchronous formatter to apply at hydration time (before form.reset()).
   * Extracted from the field's `when.formatter` or `def.formatter`.
   * Runs purely on the DTO value — no React, no side effects.
   */
  hydrateFormatter?: (value: string, prevValue?: string) => string;
}

// ---------------------------------------------------------------------------
// Public API — Outbound (form → API)
// ---------------------------------------------------------------------------

export function sanitizePayload(
  values: Record<string, unknown>,
  layout: FormLayoutNode[],
  namespace?: string,
  options?: { mode?: string },
): Record<string, unknown> | Record<string, unknown>[] {
  const fieldMap = new Map<string, FieldMeta>();
  collectFields(layout, fieldMap);

  // UDF-only layouts have no collected fields but still need outbound conversion.
  if (fieldMap.size === 0 && collectUdfNodeInfos(layout).length === 0) {
    const hasUiOnlyKeys = UI_ONLY_ROOT_KEYS.some((key) => key in values);
    if (!hasUiOnlyKeys) return values;
    const passthrough = structuredClone(values) as Record<string, unknown>;
    if (!Array.isArray(passthrough.items)) {
      stripUiOnlyRootKeys(passthrough);
    }
    return passthrough;
  }

  // Skip pruning on edit/view — server-owned nested objects may have empty-but-valid values.
  const skipPrune = options?.mode === 'edit' || options?.mode === 'view';

  // When namespace is provided, strip it from field paths so cleanup operates
  // on flat DTO keys, then unwrap the namespace wrapper from the form values.
  if (namespace) {
    const nsPrefix = `${namespace}.`;
    const flatMap = new Map<string, FieldMeta>();
    for (const [path, meta] of fieldMap) {
      const stripped = path.startsWith(nsPrefix) ? path.slice(nsPrefix.length) : path;
      flatMap.set(stripped, meta);
    }

    const nsObject = values[namespace];
    if (nsObject != null && typeof nsObject === 'object' && !Array.isArray(nsObject)) {
      const items = (nsObject as Record<string, unknown>).items as unknown[] | undefined;
      if (Array.isArray(items)) {
        return items.map((item) => {
          if (item == null || typeof item !== 'object') return {} as Record<string, unknown>;
          const flat = structuredClone(item) as Record<string, unknown>;
          applyCleanup(flat, flatMap);
          sanitizeUdfFields(flat, layout, namespace);
          if (!skipPrune) pruneEmptyNestedObjects(flat);
          return flat;
        });
      }

      const flat = structuredClone(nsObject) as Record<string, unknown>;
      applyCleanup(flat, flatMap);
      sanitizeUdfFields(flat, layout, namespace);
      if (!skipPrune) pruneEmptyNestedObjects(flat);
      return flat as Record<string, unknown>;
    }

    // Multi-row form: items at root is the sole key — each row may have namespace wrapper.
    const rootItems = values.items as unknown[] | undefined;
    if (Array.isArray(rootItems) && Object.keys(values).length === 1) {
      return rootItems.map((item) => {
        if (item == null || typeof item !== 'object') return {} as Record<string, unknown>;
        const row = (item as Record<string, unknown>)[namespace] ?? item;
        const flat = structuredClone(row) as Record<string, unknown>;
        applyCleanup(flat, flatMap);
        sanitizeUdfFields(flat, layout, namespace);
        if (!skipPrune) pruneEmptyNestedObjects(flat);
        return flat;
      });
    }

    // Single-object form with values at root level (not nested under namespace).
    const flatValues = structuredClone(values) as Record<string, unknown>;
    stripUiOnlyRootKeys(flatValues);
    applyCleanup(flatValues, flatMap);
    sanitizeUdfFields(flatValues, layout, namespace);
    if (!skipPrune) pruneEmptyNestedObjects(flatValues);
    return flatValues;
  }

  const result = structuredClone(values);
  if (!Array.isArray(result.items)) {
    stripUiOnlyRootKeys(result);
  }

  const items = result.items as unknown[] | undefined;
  if (Array.isArray(items)) {
    for (const item of items) {
      if (item != null && typeof item === 'object') {
        applyCleanup(item as Record<string, unknown>, fieldMap);
        sanitizeUdfFields(item as Record<string, unknown>, layout);
      }
    }
  } else {
    applyCleanup(result, fieldMap);
    sanitizeUdfFields(result, layout);
  }

  if (!skipPrune) {
    if (Array.isArray(items)) {
      for (const item of items) {
        if (item != null && typeof item === 'object') {
          pruneEmptyNestedObjects(item as Record<string, unknown>);
        }
      }
    } else {
      pruneEmptyNestedObjects(result);
    }
  }

  return result;
}

/**
 * Removes nested objects where all values are null/undefined/empty-string/empty-array.
 * Called after cleanup to prevent sending `{ nested: { a: null, b: null } }` when
 * the API expects the key to be absent entirely.
 *
 * Only prunes objects whose keys are ALL null-ish. If any leaf has a value, the object stays.
 */
function pruneEmptyNestedObjects(obj: Record<string, unknown>): void {
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (val != null && typeof val === 'object' && !Array.isArray(val)) {
      // Recurse first
      pruneEmptyNestedObjects(val as Record<string, unknown>);
      // Then check if all children are empty
      const entries = Object.values(val as Record<string, unknown>);
      const allEmpty = entries.length > 0 && entries.every(
        (v) => v == null || v === '' || (Array.isArray(v) && v.length === 0) || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0),
      );
      if (allEmpty) {
        delete obj[key];
      }
    }
  }
}

/**
 * Wrap sanitized form values for ActionRegistry submit actions.
 *
 * Submit actions (CREATE_* / UPDATE_*) read `ctx.params.payload`. Repeatable
 * modes may return a bare array from sanitizePayload; constants are
 * merged into each row (array) or the root object (single).
 *
 * HTF-1060: UDF values travel inside this same `payload` as `userDefinedFields`
 * — never via a second UserDefinedFieldValues API call.
 */
export function toSubmitActionParams(
  cleaned: Record<string, unknown> | Record<string, unknown>[],
  constants?: Record<string, unknown>,
): { payload: Record<string, unknown> | Record<string, unknown>[] } {
  let body: Record<string, unknown> | Record<string, unknown>[];

  if (Array.isArray(cleaned)) {
    body = cleaned;
  } else if (
    cleaned != null &&
    typeof cleaned === 'object' &&
    Array.isArray(cleaned.items) &&
    cleaned.items.length > 0 &&
    cleaned.items.every((row: unknown) => row != null && typeof row === 'object' && !Array.isArray(row))
  ) {
    // Repeatable form: `items` is an array of row objects.
    // Unwrap so the API receives a flat array of DTOs.
    // This distinguishes repeatable rows ({ items: [{firstName: '...'}, ...] })
    // from a regular domain field named "items" ({ items: ['a', 'b'], planName: '...' })
    // where elements are primitives or arrays, not plain objects.
    body = cleaned.items as Record<string, unknown>[];
  } else {
    body = (cleaned ?? {}) as Record<string, unknown>;
  }

  if (constants && Object.keys(constants).length > 0) {
    if (Array.isArray(body)) {
      body = body.map((item) =>
        item != null && typeof item === 'object'
          ? { ...item, ...constants }
          : { ...constants },
      );
    } else {
      body = { ...body, ...constants };
    }
  }

  return { payload: body };
}

// ---------------------------------------------------------------------------
// Public API — Inbound (API → form)
// ---------------------------------------------------------------------------

export function hydratePayload(
  dto: Record<string, unknown>,
  layout: FormLayoutNode[],
  namespace?: string,
): Record<string, unknown> {
  const fieldMap = new Map<string, FieldMeta>();
  collectFields(layout, fieldMap);

  // Strip namespace prefix from field paths so they align with the flat DTO keys.
  const nsPrefix = namespace ? `${namespace}.` : '';
  const strippedMap = new Map<string, FieldMeta>();
  for (const [path, meta] of fieldMap) {
    const stripped = nsPrefix && path.startsWith(nsPrefix)
      ? path.slice(nsPrefix.length)
      : path;
    strippedMap.set(stripped, meta);
  }

  // Start with the FULL DTO — everything the API sent comes back on POST.
  // Layout-managed fields are then overwritten with hydrated values (formatted
  // for the form — dashes in SSN/phone, stringified booleans for radios, etc.).
  const hydrated = structuredClone(dto);

  for (const [dotPath, meta] of strippedMap) {
    if (dotPath.includes('.*.')) continue;

    const value = getNestedValue(dto, dotPath);

    if (meta.hidden) {
      setNestedValue(hydrated, dotPath, value ?? meta.defaultValue ?? null);
      continue;
    }

    // Auto-unwrap array-of-objects to flat string[] when arrayItemKey is declared.
    if (meta.arrayItemKey && Array.isArray(value)) {
      const key = meta.arrayItemKey;
      const unwrapped = value.map((item) =>
        item != null && typeof item === 'object'
          ? (item as Record<string, unknown>)[key]
          : item,
      );
      setNestedValue(hydrated, dotPath, unwrapped);
      continue;
    }

    setNestedValue(hydrated, dotPath, hydrateValue(value, meta));
  }

  // UDF: form state === API array shape — normalize only (no flatten).
  hydrateUdfFields(hydrated, layout, namespace);

  return hydrated;
}

// ---------------------------------------------------------------------------
// Layout tree walker — same traversal pattern as validationComposer
// ---------------------------------------------------------------------------

function collectFields(
  nodes: FormLayoutNode[],
  map: Map<string, FieldMeta>,
): void {
  for (const node of nodes) {
    switch (node.type) {
      case 'fields':
        collectFromFieldsNode(node, map);
        break;
      case 'each':
        collectFromEachNode(node, map);
        break;
      case 'section':
      case 'grid':
      case 'stack':
      case 'surface':
        collectFields(node.children, map);
        break;
      case 'tabs':
        for (const tab of node.tabs) {
          collectFields(tab.children, map);
        }
        break;
      case 'conditional':
        collectFields(node.children, map);
        if (node.else) collectFields(node.else, map);
        break;
      case 'data':
        collectFields(node.children, map);
        break;
      case 'column-group':
        for (const group of node.groups) {
          for (const entry of group.fields) {
            if ('type' in entry && (entry.type === 'spacer' || entry.type === 'section-divider' || entry.type === 'stack' || entry.type === 'inline-each' || entry.type === 'inline-action-button')) continue;
            const field = entry as FormFieldMapping;
            map.set(field.name, {
              def: field.def,
              required: Boolean(field.required),
              hidden: Boolean(field.hidden),
              adapterHint: field.overrideAdapterHint ?? field.def.adapterHint,
              arrayItemKey: field.arrayItemKey,
              defaultValue: field.defaultValue,
              hydrateFormatter: resolveHydrateFormatter(field),
            });
          }
        }
        break;
      case 'divider':
      case 'custom':
      case 'action-button':
      case 'udf':
      case 'file-list':
        break;
    }
  }
}


function collectFromFieldsNode(
  node: FormFieldsNode,
  map: Map<string, FieldMeta>,
): void {
  for (const entry of node.fields) {
    if ('type' in entry && (entry.type === 'spacer' || entry.type === 'section-divider' || entry.type === 'stack' || entry.type === 'inline-each' || entry.type === 'inline-action-button')) continue;
    const field = entry as FormFieldMapping;
    map.set(field.name, {
      def: field.def,
      required: Boolean(field.required),
      hidden: Boolean(field.hidden),
      adapterHint: field.overrideAdapterHint ?? field.def.adapterHint,
      arrayItemKey: field.arrayItemKey,
      defaultValue: field.defaultValue,
      hydrateFormatter: resolveHydrateFormatter(field),
    });
  }
}

/**
 * Resolve a hydration-time formatter from the field mapping.
 *
 * Priority:
 *   1. Unconditional `def.formatter` (function) — always applies
 *   2. Conditional `when.formatter.falseFormatter` — the "default" branch
 *      (e.g. workNumber: PHONE_US when isOtherCountryPhone is false)
 *   3. Conditional `when.formatter.trueFormatter` — fallback
 *
 * For conditional formatters, we pick the "false" branch as the hydration default
 * because conditions like `isOtherCountryPhone` are effect-computed and default
 * to falsy before effects run. The effect will re-format on interaction if needed.
 */
function resolveHydrateFormatter(
  field: FormFieldMapping,
): ((value: string, prevValue?: string) => string) | undefined {
  const defFormatter = field.def.formatter;
  if (typeof defFormatter === 'function') return defFormatter;

  const whenFormatter = field.when?.formatter;
  if (whenFormatter) {
    return whenFormatter.falseFormatter ?? whenFormatter.trueFormatter;
  }
  return undefined;
}

function collectFromEachNode(
  node: FormEachNode,
  map: Map<string, FieldMeta>,
): void {
  for (const field of node.itemFields) {
    const qualifiedPath = `${node.name}.*.${field.name}`;
    map.set(qualifiedPath, {
      def: field.def,
      required: Boolean(field.required),
      hidden: Boolean(field.hidden),
      adapterHint: field.overrideAdapterHint ?? field.def.adapterHint,
      defaultValue: field.defaultValue,
    });
  }
}

// ---------------------------------------------------------------------------
// Cleanup application
// ---------------------------------------------------------------------------

function applyCleanup(
  obj: Record<string, unknown>,
  fieldMap: Map<string, FieldMeta>,
): void {
  for (const [dotPath, meta] of fieldMap) {
    if (dotPath.includes('.*.')) {
      applyToArrayItems(obj, dotPath, meta);
    } else {
      applyToField(obj, dotPath, meta);
    }
  }
}

function applyToField(
  obj: Record<string, unknown>,
  dotPath: string,
  meta: FieldMeta,
): void {
  // Hidden fields may or may not be in form state:
  //   - Fields with `defaultValue` (constants like referenceType) are never touched by RHF
  //   - Fields populated by effects (like taName) ARE in form state via setValue
  // Preserve the existing value from form state; fall back to defaultValue for constants.
  if (meta.hidden) {
    const existing = getNestedValue(obj, dotPath);
    if (existing == null && meta.defaultValue !== undefined) {
      setNestedValue(obj, dotPath, meta.defaultValue);
    }
    return;
  }

  const value = getNestedValue(obj, dotPath);
  const cleaned = cleanValue(value, meta);
  const leaf = dotPath.split('.').pop()!;

  // Convert signature data URLs to File for multipart submission.
  if (meta.adapterHint === 'signature' && typeof cleaned === 'string' && cleaned.startsWith('data:')) {
    setNestedValue(obj, dotPath, dataUrlToFile(cleaned, `${leaf}.png`));
    return;
  }

  // Re-wrap arrayItemKey fields: ["id1", "id2"] → [{ key: "id1" }, { key: "id2" }]
  if (meta.arrayItemKey && Array.isArray(cleaned)) {
    const key = meta.arrayItemKey;
    const rewrapped = cleaned
      .filter((v) => v != null && v !== '')
      .map((v) =>
        v != null && typeof v === 'object' && !Array.isArray(v)
          ? (v as Record<string, unknown>)
          : { [key]: v },
      );
    setNestedValue(obj, dotPath, rewrapped);
  } else {
    setNestedValue(obj, dotPath, cleaned);
  }
}

function applyToArrayItems(
  obj: Record<string, unknown>,
  dotPath: string,
  meta: FieldMeta,
): void {
  const [arrayPath, , ...fieldParts] = dotPath.split('.');
  const fieldName = fieldParts.join('.');

  const arrayPathParts = arrayPath.split('.');
  let current: unknown = obj;
  for (const key of arrayPathParts) {
    if (current == null || typeof current !== 'object') return;
    current = (current as Record<string, unknown>)[key];
  }

  if (!Array.isArray(current)) return;

  // Hidden fields in repeatable rows: inject defaultValue into every row item.
  if (meta.hidden) {
    if (meta.defaultValue !== undefined) {
      for (const item of current) {
        if (item != null && typeof item === 'object') {
          (item as Record<string, unknown>)[fieldParts.join('.')] = meta.defaultValue;
        }
      }
    }
    return;
  }

  for (const item of current) {
    if (item == null || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const value = record[fieldName];
    const cleaned = cleanValue(value, meta);
    if (meta.adapterHint === 'signature' && typeof cleaned === 'string' && cleaned.startsWith('data:')) {
      record[fieldName] = dataUrlToFile(cleaned, `${fieldName}.png`);
    } else {
      record[fieldName] = cleaned;
    }
  }
}

// ---------------------------------------------------------------------------
// Hydration helpers
// ---------------------------------------------------------------------------

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  const leaf = parts.pop()!;
  let current = obj;
  for (const key of parts) {
    if (!(key in current) || current[key] == null || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }
  current[leaf] = value;
}

/**
 * Determine the type-appropriate empty value for a form field.
 * Used during hydration to coerce API nulls into values that won't
 * trigger Zod type errors on form load.
 */
export function getEmptyValueForField(meta: FieldMeta): unknown {
  const def = meta.def;

  if (shouldCoerceBoolean(def)) {
    return 'false';
  }

  const hint = meta.adapterHint ?? def.adapterHint;
  if (hint === 'checkbox' || hint === 'toggle') return false;

  const fieldType = (def as Record<string, unknown>).type as string | undefined;

  switch (fieldType) {
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'file':
      return meta.adapterHint === 'file-upload' ? [] : null;
    case 'number':
    case 'integer':
      return '';
    case 'object':
      return {};
    case 'string':
    default:
      return '';
  }
}

function hydrateValue(value: unknown, meta: FieldMeta): unknown {
  const resolvedHint = meta.adapterHint ?? meta.def.adapterHint;
  if (shouldStripDashes(meta.def, resolvedHint)) {
    if (typeof value === 'string' && value) {
      if (meta.def.displayFormat === 'ssn') return formatSsn(value);
      if (resolvedHint === 'phone') return formatPhone(value);
    }
    return value ?? '';
  }

  if (meta.hydrateFormatter && typeof value === 'string' && value) {
    return meta.hydrateFormatter(value);
  }

  if (shouldCoerceBoolean(meta.def)) {
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (value === 'true' || value === 'false') return value;
    return getEmptyValueForField(meta);
  }

  if (value == null) {
    return getEmptyValueForField(meta);
  }

  const expectedType = (meta.def as Record<string, unknown>).type as string | undefined;

  if (expectedType === 'array' && !Array.isArray(value)) {
    return [];
  }

  if (expectedType === 'boolean' && typeof value !== 'boolean') {
    if (value === 'true' || value === 1) return true;
    return false;
  }

  if (!meta.required && typeof value === 'string' && value.trim() === '') {
    return null;
  }

  return value;
}

function formatSsn(value: string): string {
  const n = value.replace(/[^\d]/g, '');
  if (n.length !== 9) return value;
  return `${n.slice(0, 3)}-${n.slice(3, 5)}-${n.slice(5, 9)}`;
}

function formatPhone(value: string): string {
  const n = value.replace(/[^\d]/g, '');
  if (n.length < 7) return value;
  return `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6, 10)}`;
}

// ---------------------------------------------------------------------------
// Per-field value cleanup (outbound)
// ---------------------------------------------------------------------------

function cleanValue(
  value: unknown,
  ...args: [FieldMeta] | [Record<string, unknown>, FieldMeta]
): unknown {
  const meta = args.length === 2 ? args[1] : args[0];

  if (value == null) return null;

  if (shouldStripDashes(meta.def, meta.adapterHint)) {
    if (typeof value === 'string' && value) {
      return value.replace(/-/g, '') || null;
    }
    return value || null;
  }

  if (shouldCoerceBoolean(meta.def)) {
    return value === true || value === 'true';
  }

  if (shouldNormalizeDateOnly(meta.adapterHint)) {
    if (typeof value === 'string' && value) {
      return toDateOnly(value);
    }
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!meta.required && trimmed === '') return null;
    return trimmed;
  }

  return value;
}

// ---------------------------------------------------------------------------
// UDF — form state === API shape (HTF-1060)
//
// `{ userDefinedFields: [{ name, value, moduleId }] }` in both directions.
// No flatten / __udfMeta. hydrate/sanitize only ensure array + string values.
// ---------------------------------------------------------------------------

interface UdfNodeInfo {
  fieldPrefix: string;
  moduleId: number;
}

function collectUdfNodeInfos(nodes: FormLayoutNode[]): UdfNodeInfo[] {
  const result: UdfNodeInfo[] = [];
  walkUdfInfos(nodes, result);
  return result;
}

function walkUdfInfos(nodes: FormLayoutNode[], out: UdfNodeInfo[]): void {
  for (const node of nodes) {
    switch (node.type) {
      case 'udf': {
        const udf = node as FormUdfNode;
        out.push({
          fieldPrefix: udf.fieldPrefix ?? 'userDefinedFields',
          moduleId: udf.moduleId,
        });
        break;
      }
      case 'section':
      case 'grid':
      case 'stack':
      case 'surface':
        walkUdfInfos(node.children, out);
        break;
      case 'tabs':
        for (const tab of node.tabs) walkUdfInfos(tab.children, out);
        break;
      case 'conditional':
        walkUdfInfos(node.children, out);
        if (node.else) walkUdfInfos(node.else, out);
        break;
      default:
        break;
    }
  }
}

function resolveUdfKey(fieldPrefix: string, namespace?: string): string {
  const nsPrefix = namespace ? `${namespace}.` : '';
  return nsPrefix && fieldPrefix.startsWith(nsPrefix)
    ? fieldPrefix.slice(nsPrefix.length)
    : fieldPrefix;
}

function normalizeUdfArray(
  raw: unknown,
  moduleId: number,
): Array<{ name: string; value: string; moduleId: number }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ name: string; value: string; moduleId: number }> = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const row = item as { name?: unknown; value?: unknown; moduleId?: unknown };
    if (typeof row.name !== 'string' || row.name.length === 0) continue;
    out.push({
      name: row.name,
      value: row.value == null ? '' : String(row.value),
      moduleId: typeof row.moduleId === 'number' ? row.moduleId : moduleId,
    });
  }
  return out;
}

/**
 * Inbound: keep API array as form state (same shape). Missing → [].
 */
function hydrateUdfFields(
  dto: Record<string, unknown>,
  layout: FormLayoutNode[],
  namespace?: string,
): void {
  const udfNodes = collectUdfNodeInfos(layout);
  if (!udfNodes.length) return;

  for (const udfNode of udfNodes) {
    const key = resolveUdfKey(udfNode.fieldPrefix, namespace);
    dto[key] = normalizeUdfArray(dto[key], udfNode.moduleId);
  }
}

/**
 * Outbound: same array shape — normalize values/moduleId only.
 */
function sanitizeUdfFields(
  dto: Record<string, unknown>,
  layout: FormLayoutNode[],
  namespace?: string,
): void {
  const udfNodes = collectUdfNodeInfos(layout);
  if (!udfNodes.length) return;

  for (const udfNode of udfNodes) {
    const key = resolveUdfKey(udfNode.fieldPrefix, namespace);
    dto[key] = normalizeUdfArray(dto[key], udfNode.moduleId);
  }
}

// ---------------------------------------------------------------------------
// Rule predicates — driven by FieldDef metadata
// ---------------------------------------------------------------------------

function shouldStripDashes(def: FieldDef, resolvedHint?: string): boolean {
  const hint = resolvedHint ?? def.adapterHint;
  return hint === 'phone' || def.displayFormat === 'ssn';
}

function shouldCoerceBoolean(def: FieldDef): boolean {
  if (def.adapterHint !== 'radio') return false;
  const fieldType = (def as Record<string, unknown>).type as string | undefined;
  return fieldType === 'boolean';
}

function shouldNormalizeDateOnly(adapterHint?: string): boolean {
  return adapterHint === 'date' || adapterHint === 'datepicker';
}

/**
 * Normalizes a date string to YYYY-MM-DD, stripping any time component.
 * Handles ISO timestamps ("2000-08-13T00:00:00", "2000-08-13T18:15:00.000Z")
 * and already-clean dates ("2000-08-13").
 */
function toDateOnly(value: string): string {
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : value;
}

/** Convert a base64 data URL to a File for multipart form submission. */
function dataUrlToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header?.match(/data:([^;]+)/);
  const mime = mimeMatch?.[1] ?? 'image/png';
  const binary = atob(base64 ?? '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
}
